import { useState, useEffect } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import TopNav from "./TopNav";
import TermsGate from "./TermsGate";
import BriefSaveWarning from "./BriefSaveWarning";
import { useAuth } from "../contexts/AuthContext";
import { pendingSubmitCount } from "../lib/submitQueue";
import { refreshAnnouncements } from "../data/announcements";
import { installKeepShellPinned } from "../lib/keepShellPinned";

export default function AuthenticatedLayout({ children, onLogout, userRoles = [] }) {
  const { offline, needsReauth } = useAuth();
  const navigate = useNavigate();
  // Queued submissions are shown until they actually send — a pilot must be
  // able to glance and know "my slip is going, nothing else to do."
  const [queuedSubmits, setQueuedSubmits] = useState(() => pendingSubmitCount());
  useEffect(() => {
    const update = () => setQueuedSubmits(pendingSubmitCount());
    window.addEventListener("submit-queue-changed", update);
    window.addEventListener("online", update);
    const iv = setInterval(update, 20000);
    return () => {
      window.removeEventListener("submit-queue-changed", update);
      window.removeEventListener("online", update);
      clearInterval(iv);
    };
  }, []);
  // Posted-announcements cache: one fail-silent, 5s-deadlined refresh per
  // login (and again when the device comes back online). Readers stay pure
  // localStorage — this is the only network touch in the What's New plumbing.
  useEffect(() => {
    refreshAnnouncements();
    window.addEventListener("online", refreshAnnouncements);
    return () => window.removeEventListener("online", refreshAnnouncements);
  }, []);
  // Phones: if iOS shifts the window (keyboard open/close), put it back once
  // the field blurs. See keepShellPinned.js.
  useEffect(() => installKeepShellPinned(), []);
  // Below 820px the .app-shell rules in kit.css pin this div to the viewport
  // and make <main> the scroller, with TopNav's bottom tab bar as a flow
  // sibling below it (the fix for the iOS drifting-bar bug). Desktop is
  // untouched: no media match, plain block flow, the window scrolls.
  return (
    <div className="app-shell">
      <TopNav onLogout={onLogout} userRoles={userRoles} />
      <TermsGate />
      <BriefSaveWarning />
      {offline && (
        <div
          style={{
            background: "var(--warn-bg)",
            color: "var(--warn-fg)",
            borderBottom: "1px solid var(--warn-border)",
            padding: "6px 16px",
            fontSize: "0.85rem",
            fontWeight: 600,
            textAlign: "center",
          }}
        >
          Offline: showing last-loaded data. Changes may not save until you're back online.
        </div>
      )}
      {queuedSubmits > 0 && (
        <div
          style={{
            background: "var(--info-bg, var(--warn-bg))",
            color: "var(--info-fg, var(--warn-fg))",
            borderBottom: "1px solid var(--info-border, var(--warn-border))",
            padding: "6px 16px",
            fontSize: "0.85rem",
            fontWeight: 600,
            textAlign: "center",
          }}
        >
          {queuedSubmits === 1
            ? "1 submission is queued on this device and will send itself automatically when you're online."
            : `${queuedSubmits} submissions are queued on this device and will send themselves automatically when you're online.`}
        </div>
      )}
      {!offline && needsReauth && (
        <div
          style={{
            background: "var(--warn-bg)",
            color: "var(--warn-fg)",
            borderBottom: "1px solid var(--warn-border)",
            padding: "6px 16px",
            fontSize: "0.85rem",
            fontWeight: 600,
            textAlign: "center",
          }}
        >
          This device is signed out. Your work is saved on it.
          {" "}
          <button
            onClick={() => navigate("/login?reauth=1")}
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              textDecoration: "underline",
              fontWeight: 700,
              cursor: "pointer",
              padding: 0,
              fontSize: "inherit",
            }}
          >
            Sign in to sync it to your account.
          </button>
        </div>
      )}
      <main className="app-main" style={{ paddingTop: "1rem", paddingBottom: "1rem" }}>
        {children ? children : <Outlet />}
      </main>
    </div>
  );
}
